import { useAppStore } from '@/stores/app';
import { useUserStore } from '@/stores/user';
import { IApiListParams, IBaseApiListResponse, IListParams } from '@/types';
import { useLeagueStore } from '@/stores/league';
import { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import isEmpty from 'lodash-es/isEmpty';
import { useEventStore } from '@/stores/event';
import { useTeamStore } from '@/stores/team';
import { globals } from '@/globals';
import { useWebSocketStore } from '@/stores/webSocket';
import { setRemoteDictionaries } from '@/helpers/dictionary';
import { pageReload } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { usePlatform } from '@/composables/usePlatform';

export async function baseListService<T extends IBaseApiListResponse>(apiCallFn: (params: IApiListParams) => Promise<AxiosResponse<T>>, params: IListParams): Promise<AxiosResponse<T>> {
    const appStore = useAppStore();
    const userStore = useUserStore();
    const teamStore = useTeamStore();
    const eventStore = useEventStore();
    const leagueStore = useLeagueStore();
    const webSocketStore = useWebSocketStore();
    const platform = usePlatform();

    console.log('Load data from server');

    const start = (params.options.page - 1) * params.options.itemsPerPage;
    const sort = params.options.sortBy.map(({ key, order }) => ({
        property: key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase(), // convert to kebab-case
        direction: order,
    }));

    //const filters = Object.fromEntries(Object.entries(params.filters).filter(([, v]) => v !== '' && v !== null && (!Array.isArray(v) || v.length !== 0)));

    let filters: Record<string, any> = {};

    if (params.filters) {
        filters = Object.fromEntries(
            Object.entries(params.filters)
                .filter(([, v]) => v !== '' && v !== null && (!Array.isArray(v) || v.length !== 0))
                .flatMap(([key, value]) => {
                    if (key === 'date' && Array.isArray(value) && value.length === 2) {
                        return [
                            ['date_from', format(value[0], 'yyyy-MM-dd') + 'T00:00:00 GMT' + userStore.getTimezoneOffset()],
                            ['date_to', format(value[1], 'yyyy-MM-dd') + 'T23:59:59 GMT' + userStore.getTimezoneOffset()],
                        ];
                    }
                    return [[key, value]];
                })
        );
    }

    let requiredDictionaries = cloneDeep(params.requiredDictionaries).filter((item) => item !== 'TimeZone' && item !== 'Site');
    // requiredDictionaries.push('TimeZone');
    if (platform.isAggregator()) {
        requiredDictionaries.push('Site');
    }

    let paramsData: any = {
        page: params.options.page,
        filter: filters,
        start,
        limit: params.options.itemsPerPage,
        sort: JSON.stringify(sort),
        requiredDictionaries: requiredDictionaries,
        requiredData: params.requiredData,
    };

    if (!appStore.isInitialized) {
        paramsData.init = true;
    }

    if (params.extraParams) {
        Object.assign(paramsData, params.extraParams);
    }

    const pageUrl = window.location.toString();

    try {
        const response = await apiCallFn(paramsData);
        if (response.data.success !== false) {
            const data = response.data;

            if (data.config?.scriptVersion) {
                const oldVersion = localStorage.getItem('scriptVersion');
                if (oldVersion !== data.config.scriptVersion.toString()) {
                    localStorage.setItem('scriptVersion', data.config.scriptVersion.toString());
                    if (oldVersion) pageReload();
                }
            }

            if (data.balance) userStore.setBalance(data.balance);
            if (data.config && !Array.isArray(data.config)) {
                if (data.config.isDepositWithdrawalEnabled) appStore.config.isDepositWithdrawalEnabled = data.config.isDepositWithdrawalEnabled;
                if (data.config.minStakes) appStore.config.minStakes = data.config.minStakes;
                if (data.config.scriptVersion) appStore.config.scriptVersion = data.config.scriptVersion;

                if (data.config.toolUserSettings) userStore.settings = Object.assign(userStore.settings, data.config.toolUserSettings);
            }
            if (data.dictionary) setRemoteDictionaries(data.dictionary);
            if (data.notification) appStore.setNotifications(data.notification);
            if (data.favoriteEvents) eventStore.setFavoriteEvents(data.favoriteEvents);
            if (data.favoriteLeagues) leagueStore.setFavoriteLeagues(data.favoriteLeagues);
            if (data.favoriteTeams) teamStore.setFavoriteTeams(data.favoriteTeams);
            if (data.bets) appStore.setBets(data.bets);
            if (data.orders) appStore.setOrders(data.orders);

            appStore.isInitialized = true;

            if (params.updateUserData !== false && pageUrl === window.location.toString()) {
                let payload = {
                    pageUrl: pageUrl,
                    pageCode: globals.router.currentRoute.value.name,
                    filter: !isEmpty() ? filters : undefined,
                };

                webSocketStore.sendObj({
                    event: 'updateUserData',
                    data: payload,
                });

                webSocketStore.setUserData(payload);
            }
        }

        return response;
    } catch (e) {
        throw e;
    }
}

export async function baseListExportService(apiCallFn: (params: IApiListParams) => Promise<AxiosResponse<Blob>>, params: IListParams): Promise<AxiosResponse<Blob>> {
    const userStore = useUserStore();

    console.log('Load export data from server');

    const sort = [{ property: 'created_at', direction: 'desc' }];

    //const filters = Object.fromEntries(Object.entries(params.filters).filter(([, v]) => v !== '' && v !== null && (!Array.isArray(v) || v.length !== 0)));

    let filters: Record<string, any> = {};

    if (params.filters) {
        filters = Object.fromEntries(
            Object.entries(params.filters)
                .filter(([, v]) => v !== '' && v !== null && (!Array.isArray(v) || v.length !== 0))
                .flatMap(([key, value]) => {
                    if (key === 'date' && Array.isArray(value) && value.length === 2) {
                        return [
                            ['date_from', format(value[0], 'yyyy-MM-dd') + 'T00:00:00 GMT' + userStore.getTimezoneOffset()],
                            ['date_to', format(value[1], 'yyyy-MM-dd') + 'T23:59:59 GMT' + userStore.getTimezoneOffset()],
                        ];
                    }
                    return [[key, value]];
                })
        );
    }

    let paramsData: any = {
        page: params.options.page,
        filter: filters,
        start: 0, //export all data
        limit: params.options.itemsPerPage,
        sort: JSON.stringify(sort),
        toXls: 1,
        columns: params.columns,
    };

    if (params.extraParams) {
        Object.assign(paramsData, params.extraParams);
    }

    try {
        return await apiCallFn(paramsData);
    } catch (e) {
        throw e;
    }
}
